import noop from "@/services/noop";
import { crux_bolt_type_e } from "@WahooFitness/crux-js-display-cfg";
import {
  WfIconContentArrowArcLeftRegular,
  WfIconNavigationChevronLeftRegular,
  WfIconNavigationCloseRegular,
  WfIconNavigationOverflowBold,
} from "@WahooFitness/redesignr";
import {
  ErrorBoundary,
  GlobalHeader,
  useDialogContext,
  useHeaderContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { Trans, t } from "@lingui/macro";
import { Container, ListItemIcon, Menu, MenuItem, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { FocusEvent, MouseEvent, useCallback, useMemo, useState } from "react";
import useEditWorkoutPage from "./useEditWorkoutPage";
import useWorkoutPageCategoryStrings from "./useWorkoutPageCategoryStrings";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { motion } from "motion/react";
import AddDataFieldDrawer from "./Drawers/AddDataFieldDrawer";
import EmptyField from "./FieldComponents/EmptyField";
import SortableWorkoutFieldsList from "./FieldComponents/SortableWorkoutFieldItem";
import StaticWorkoutFields from "./FieldComponents/StaticWorkoutFields";
import WorkoutPageField from "./FieldComponents/WorkoutPageField";
import { SENSOR_CONFIG } from "../../../hooks/useDragAndDrop";
import { WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";

const EditWorkoutPage = ({
  profile,
  pageId,
  elevation = false,
}: {
  profile?: WSMWorkoutProfile;
  pageId?: number;
  elevation?: boolean;
}) => {
  const {
    pagesAreLoading,
    pageName,
    pageNameError,
    displayFields,
    cruxPermanentDisplayFields,
    maxFields,
    minFields,
    maxFieldDevices,
    fieldsCount,
    openCategoryDialog,
    closeCategoryDialog,
    changingPedalMonitorGraphType,
    removeField,
    categoryDialogOpen,
    setFieldToReplace,
    selectedCategories,
    setSelectedCategories,
    lastSelectedCategory,
    searchTerm,
    handleChangeSearchTerm,
    dialogMenuItems,
    pedalMonitorGraphTypeMenuItems,
    pageNameInputValue,
    handlePageNameChange,
    handlePageNameBlur,
    moveField,
    flushPendingSave,
    isMapPage,
    isPedalMonitorPage,
    showElevationProfile,
    toggleShowElevationProfile,
    pedalMonitorGraphType,
    handlePageReset,
    page,
    isPlannedWorkoutPage,
  } = useEditWorkoutPage(profile, pageId, elevation);

  const { navigateBack } = useHeaderContext();
  const [activeId, setActiveId] = useState<string | null>(null);

  const { workoutPageCategoryTranslations } = useWorkoutPageCategoryStrings();

  const backAction = useCallback(() => {
    if (selectedCategories.length > 0) {
      setSelectedCategories((current) => current.slice(0, current.length - 1));
    } else {
      closeCategoryDialog();
    }
  }, [selectedCategories.length, closeCategoryDialog, setSelectedCategories]);

  const highlightText = useCallback(
    (event: FocusEvent<HTMLInputElement>) => event.target.select(),
    []
  );

  const sensors = useSensors(
    useSensor(TouchSensor, SENSOR_CONFIG),
    useSensor(MouseSensor, SENSOR_CONFIG)
  );

  const sortableFields = useMemo(
    () => displayFields.filter((f) => !cruxPermanentDisplayFields.includes(f)),
    [displayFields, cruxPermanentDisplayFields]
  );

  const sortableIds = useMemo(
    () => sortableFields.map((f) => f.getId().toString()),
    [sortableFields]
  );

  const idToDisplayFieldIndex = useMemo(() => {
    const map = new Map<string, number>();
    displayFields.forEach((field, i) => {
      map.set(field.getId().toString(), i);
    });
    return map;
  }, [displayFields]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const fromIndex = idToDisplayFieldIndex.get(String(active.id));
      const toIndex = idToDisplayFieldIndex.get(String(over.id));
      if (fromIndex == null || toIndex == null) return;

      moveField(fromIndex, toIndex);
      setActiveId(null);
    },
    [idToDisplayFieldIndex, moveField]
  );

  const minFieldWarning = useMemo(() => {
    if (profile?.workout_type_family_id !== WahooWorkoutTypeFamily.Cycling) {
      return undefined;
    }
    if (minFields !== 0 && maxFields > minFields) {
      const minFieldDevs = maxFieldDevices[minFields];
      type BoltTypeValue = (typeof crux_bolt_type_e)[keyof typeof crux_bolt_type_e];

      const deviceGroups: { label: string; types: BoltTypeValue[] }[] = [
        {
          label: "BOLT",
          types: [crux_bolt_type_e.BOLT, crux_bolt_type_e.BOLT_2, crux_bolt_type_e.BOLT_3],
        },
        {
          label: "ROAM",
          types: [
            crux_bolt_type_e.ROAM,
            crux_bolt_type_e.ROAM_1_1,
            crux_bolt_type_e.ROAM_2,
            crux_bolt_type_e.ROAM_3,
          ],
        },
      ];

      const deviceList = deviceGroups
        .filter(({ types }) => types.some((t) => minFieldDevs.has(t)))
        .map(({ label }) => label);

      if (deviceList.length === 0) return undefined;

      const and = t`and`;
      return t`Only the first ${minFields} fields will appear on ${deviceList.join(` ${and} `)}.`;
    }
    return undefined;
  }, [profile?.workout_type_family_id, maxFields, minFields, maxFieldDevices]);

  const drawerHeaderProps = useMemo(() => {
    if (lastSelectedCategory) {
      return {
        title: workoutPageCategoryTranslations[lastSelectedCategory.getId()],
        primaryLeftActionItem: {
          icon: <WfIconNavigationChevronLeftRegular />,
          action: backAction,
        },
      };
    }
    return {
      title: t`Choose data field`,
      primaryLeftActionItem: {
        icon: <WfIconNavigationCloseRegular />,
        action: closeCategoryDialog,
      },
    };
  }, [backAction, closeCategoryDialog, lastSelectedCategory, workoutPageCategoryTranslations]);

  const handleBackAction = useCallback(async () => {
    await flushPendingSave();
    navigateBack();
  }, [flushPendingSave, navigateBack]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleMenuClose = useCallback(() => setAnchorEl(null), []);

  const headerMenu = useMemo(
    () => ({ icon: <WfIconNavigationOverflowBold />, action: handleMenuOpen }),
    [handleMenuOpen]
  );

  const { setDialog, handleClose } = useDialogContext();

  const handlePageResetClick = useCallback(() => {
    handleMenuClose();
    setDialog({
      open: true,
      title: t`Reset page?`,
      body: t`This will reset the page to its default fields and layout.`,
      actions: [
        { text: t`Cancel` },
        {
          text: t`Reset`,
          color: "error",
          action: () => {
            handleClose();
            handlePageReset();
          },
        },
      ],
    });
  }, [handleClose, handleMenuClose, handlePageReset, setDialog]);

  const handleDragStart = useCallback(async (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const remainingFieldCount = useMemo(() => maxFields - fieldsCount, [maxFields, fieldsCount]);
  const displayedFieldCount = useMemo(() => {
    return maxFields - cruxPermanentDisplayFields.length;
  }, [cruxPermanentDisplayFields.length, maxFields]);

  const drawerMenuItems = useMemo(() => {
    if (changingPedalMonitorGraphType) {
      return pedalMonitorGraphTypeMenuItems;
    }
    return dialogMenuItems;
  }, [changingPedalMonitorGraphType, dialogMenuItems, pedalMonitorGraphTypeMenuItems]);

  return (
    <>
      <GlobalHeader
        title={pageName ?? t`Workout pages`}
        backAction={handleBackAction}
        primaryRightActionItem={headerMenu}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary error={undefined} isLoading={pagesAreLoading}>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            MenuListProps={{ "aria-labelledby": "basic-button" }}
          >
            <MenuItem onClick={handlePageResetClick}>
              <ListItemIcon>
                <WfIconContentArrowArcLeftRegular color="primary" />
              </ListItemIcon>
              <Trans>Reset to default</Trans>
            </MenuItem>
          </Menu>

          <Box display="flex" flexDirection="column" minHeight="100%" width="100%" gap={1} p={2}>
            <Box width="100%" py={1}>
              <TextField
                value={pageNameInputValue}
                label={t`Page name`}
                variant="outlined"
                size="medium"
                fullWidth
                onFocus={highlightText}
                onChange={handlePageNameChange}
                onBlur={handlePageNameBlur}
                helperText={pageNameError && <Typography color="error">{pageNameError}</Typography>}
              />
            </Box>

            <StaticWorkoutFields
              isMapPage={isMapPage}
              showElevationProfile={showElevationProfile}
              isPedalMonitorPage={isPedalMonitorPage}
              toggleShowElevationProfile={toggleShowElevationProfile}
              cruxPermanentDisplayFields={cruxPermanentDisplayFields}
              pedalMonitorGraphType={pedalMonitorGraphType}
              openCategoryDialog={openCategoryDialog}
              isPlannedWorkoutPage={isPlannedWorkoutPage}
              workoutProfile={profile}
            />
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} my={2} mx={2}>
              <Typography variant="prose-base-bold" textAlign="center">
                <Trans>
                  Choose up to {displayedFieldCount} data fields from most to least important.
                </Trans>
              </Typography>

              {minFieldWarning && (
                <Typography variant="prose-sm" textAlign="center">
                  {minFieldWarning}
                </Typography>
              )}
            </Box>
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={onDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                {sortableFields.length > 0 && (
                  <Box display="flex" flexDirection="column" borderRadius={2}>
                    <SortableWorkoutFieldsList
                      fields={sortableFields}
                      page={page}
                      openCategoryDialog={openCategoryDialog}
                      removeField={removeField}
                    />
                  </Box>
                )}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeId && (
                  <motion.div
                    style={{ borderRadius: 12 }}
                    initial={{ scale: 1 }}
                    animate={{
                      scale: 1.05,
                      boxShadow:
                        "0 3px 14px 2px rgba(0, 0, 0, 0.08), 0 8px 10px 1px rgba(0, 0, 0, 0.08), 0 5px 5px -3px rgba(0, 0, 0, 0.12)",
                    }}
                    transition={{ type: "spring", duration: 0.2 }}
                  >
                    <WorkoutPageField
                      id={activeId}
                      field={sortableFields.find((f) => f.getId().toString() === activeId)}
                      isEditable={true}
                      onFieldClick={noop}
                      onFieldRemove={noop}
                      isFirstField={true}
                      isLastField={true}
                    />
                  </motion.div>
                )}
              </DragOverlay>
            </DndContext>
            {remainingFieldCount > 0 && (
              <Box display="flex" flexDirection="column" mt={2} borderRadius={2}>
                {Array.from({ length: remainingFieldCount }).map((_, i) => (
                  <EmptyField
                    isLastField={i === remainingFieldCount - 1}
                    key={i}
                    openCategoryDialog={openCategoryDialog}
                    fieldPosition={fieldsCount + i + 1}
                  />
                ))}
              </Box>
            )}
          </Box>

          <AddDataFieldDrawer
            open={categoryDialogOpen}
            handleClose={() => {
              setFieldToReplace(undefined);
              setSelectedCategories([]);
              closeCategoryDialog();
            }}
            lastSelectedCategory={lastSelectedCategory}
            changingPedalMonitorGraphType={changingPedalMonitorGraphType}
            searchTerm={searchTerm}
            handleChangeSearchTerm={handleChangeSearchTerm}
            menuItems={drawerMenuItems}
            headerProps={drawerHeaderProps}
          />
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default EditWorkoutPage;
